import * as React from 'react';
import {
  HeadingText,
  ParagraphText,
  StackLayout,
  GDSHeadingLevel,
  GDSHeadingSize,
} from '@leagueplatform/genesis-core';
import { useUniqueId } from '@leagueplatform/web-common';
import { Accordion } from '@leagueplatform/genesis-core-alpha';
import {
  TableComponent,
  CoverageTable,
} from './coverage-details-widget-table.component';

interface SectionAccordion {
  isExpanded?: boolean;
  id?: string;
  dividerPosition?: 'top' | 'bottom' | 'both' | 'none';
}

export interface Section {
  heading?: string;
  headingLevel?: GDSHeadingLevel;
  headingSize?: GDSHeadingSize;
  accordion?: SectionAccordion;
  description?: string;
  table?: CoverageTable;
}

export const SectionComponent: React.FC<{
  section: Section;
  sectionIndex: number;
  onClick: (sectionIndex: number, newExpandedState: boolean) => void;
}> = ({ section, sectionIndex, onClick: onClickCallback }) => {
  const {
    heading,
    headingLevel = '3',
    headingSize = 'md',
    accordion,
    description,
    table,
  } = section;
  const sectionId = useUniqueId({ prefix: 'coverage-details-section-' });
  const accordionId = accordion?.id ?? `${sectionId}-accordion`;
  const isExpanded = accordion?.isExpanded ?? false;

  const handleAccordionClick = React.useCallback(() => {
    if (onClickCallback) {
      onClickCallback(sectionIndex, !isExpanded);
    }
  }, [sectionIndex, isExpanded, onClickCallback]);

  const sectionContent = (
    <StackLayout css={{ width: '100%' }} spacing="$one">
      {description && <ParagraphText>{description}</ParagraphText>}
      {table && <TableComponent table={table} />}
    </StackLayout>
  );

  if (accordion && heading) {
    return (
      <Accordion
        heading={heading}
        headingLevel={headingLevel}
        headingSize={headingSize}
        defaultExpanded={accordion.isExpanded}
        isExpanded={isExpanded}
        id={accordionId}
        onClick={handleAccordionClick}
        css={{
          width: '100%',
          marginBlockStart: '$none',
          borderBlockStartColor: '$onSurfaceBorderSubdued',
        }}
        dividerPosition={accordion.dividerPosition}
      >
        {sectionContent}
      </Accordion>
    );
  }

  return (
    <StackLayout css={{ width: '100%' }} spacing="$one">
      {heading && (
        <HeadingText level={headingLevel} size={headingSize}>
          {heading}
        </HeadingText>
      )}
      {sectionContent}
    </StackLayout>
  );
};
